//
// Public "pay this invoice" page, reached from the Pay Now button in the
// invoice email. No login: the token in the URL authorizes it. See
// kbt-backend/docs/superpowers/specs/2026-09-30-invoice-pay-link-design.md.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import EditorialSection from '../components/editorial/EditorialSection';
import InvoiceCheckout from '../components/stripe/InvoiceCheckout';
import { lookupPayInvoice, createPayInvoiceIntent, createPayInvoicePlan } from '../api/publicInvoices';
import { PAY_STATES, payStateForInvoice, payStateForError } from '../utils/payInvoiceState';
import { RETURN_PATHS } from '../service/checkoutReturn';
import { laToday, addDays, longDateYmd, ordinalDay } from '../utils/payPlanDates';

const ACH_FIRST = ['us_bank_account', 'card'];
const OFFICE = 'accounting@kbtla.org or 310-363-0889';

const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const longDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const PRESETS = [3, 6, 12];
const MAX_START_DAYS = 60;

// 'full' | 3 | 6 | 12 | 'custom'
function usePlanChoice(invoice) {
  const [choice, setChoice] = useState('full');
  const [customCount, setCustomCount] = useState('');
  const options = invoice?.planOptions || [];
  const maxCount = options.length ? Math.max(...options.map((o) => o.installments)) : 0;

  let installments = null; // null = pay in full
  let error = null;
  if (typeof choice === 'number') installments = choice;
  if (choice === 'custom' && customCount !== '') {
    const n = Number(customCount);
    if (!Number.isInteger(n) || n < 1) error = `Choose between 2 and ${maxCount} payments.`;
    else if (n === 1) installments = null;
    else if (!options.some((o) => o.installments === n)) error = `Choose between 2 and ${maxCount} payments.`;
    else installments = n;
  }
  const option = installments ? options.find((o) => o.installments === installments) : null;
  return { choice, setChoice, customCount, setCustomCount, installments, option, error, options };
}

const amountsText = (option) =>
  `${option.installments} payments of ${money(option.firstCents / 100)}` +
  (option.finalCents !== option.firstCents ? `, then ${money(option.finalCents / 100)} on the last one` : '');

const monthlyText = (startDate) => {
  const day = Number(startDate.split('-')[2]);
  return `monthly on or about the ${ordinalDay(startDate)}${day >= 29 ? ' (or the last day of shorter months)' : ''}`;
};

const MESSAGES = {
  [PAY_STATES.PROCESSING]: {
    title: "We've received your bank payment.",
    body: 'Bank transfers usually clear within 4 business days. You will get a receipt by email once it does. There is nothing more you need to do.',
  },
  [PAY_STATES.PAID]: {
    title: 'This invoice is paid.',
    body: 'Thank you. Your receipt was emailed to you.',
  },
  [PAY_STATES.UNAVAILABLE]: {
    title: 'This invoice cannot be paid online.',
    body: `Please contact the office at ${OFFICE}.`,
  },
  [PAY_STATES.INVALID]: {
    title: 'This payment link is no longer valid.',
    body: `Please use the link in your most recent invoice email, or contact the office at ${OFFICE}.`,
  },
  [PAY_STATES.RATE_LIMITED]: {
    title: 'Too many attempts.',
    body: 'Please wait a few minutes and try again.',
  },
  [PAY_STATES.ERROR]: {
    title: 'Something went wrong.',
    body: `Please try again in a moment. If it keeps happening, contact the office at ${OFFICE}.`,
  },
};

function InvoiceSummary({ invoice, asH1 = false, totalLabel = 'Total' }) {
  const due = longDate(invoice.dueDate);
  const partlyPaid = Number(invoice.amountPaid) > 0;
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Invoice
        </Typography>
        {/* Only one h1 per page: this is it when there's no status message
            above (the PAYABLE state); otherwise the message title is the h1
            and this stays a plain paragraph. */}
        <Typography variant="h5" component={asH1 ? 'h1' : 'p'}>
          {asH1 ? `Invoice ${invoice.invoiceNumber}` : invoice.invoiceNumber}
        </Typography>
        {invoice.memberName && <Typography>For {invoice.memberName}</Typography>}
        {due && <Typography sx={{ color: 'text.secondary' }}>Due {due}</Typography>}
      </Box>
      <Divider />
      {invoice.lineItems.map((item, i) => (
        <Stack key={i} direction="row" justifyContent="space-between" spacing={2}>
          <Typography>
            {item.description}
            {item.quantity > 1 ? ` × ${item.quantity}` : ''}
          </Typography>
          <Typography>{money(item.quantity * item.unitPrice)}</Typography>
        </Stack>
      ))}
      {invoice.tax > 0 && (
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary' }}>Tax ({invoice.taxRate}%)</Typography>
          <Typography sx={{ color: 'text.secondary' }}>{money(invoice.tax)}</Typography>
        </Stack>
      )}
      <Divider />
      {partlyPaid && (
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary' }}>Already paid</Typography>
          <Typography sx={{ color: 'text.secondary' }}>-{money(invoice.amountPaid)}</Typography>
        </Stack>
      )}
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" component="p">
          {totalLabel}
        </Typography>
        <Typography variant="h6" component="p">
          {money(partlyPaid && invoice.balance != null ? invoice.balance : invoice.total)}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function PayInvoice() {
  const { token } = useParams();
  const [state, setState] = useState(PAY_STATES.LOADING);
  const [invoice, setInvoice] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [intent, setIntent] = useState('payment');
  const [startDate, setStartDate] = useState(() => laToday());
  const plan = usePlanChoice(invoice);
  const today = laToday();
  const dateOk = startDate >= today && startDate <= addDays(today, MAX_START_DAYS);

  useEffect(() => {
    let live = true;
    lookupPayInvoice(token)
      .then((res) => {
        if (!live) return;
        setInvoice(res.data.invoice);
        setState(payStateForInvoice(res.data.invoice));
      })
      .catch((err) => {
        if (!live) return;
        setState(payStateForError(err));
      });
    return () => {
      live = false;
    };
  }, [token]);

  // The PaymentIntent (or plan SetupIntent) is created only when the payer
  // chooses to continue, not on every page view.
  const startPayment = async () => {
    setStarting(true);
    setStartError(null);
    const isPlan = Boolean(plan.installments);
    try {
      const res = isPlan
        ? await createPayInvoicePlan(token, { installments: plan.installments, startDate })
        : await createPayInvoiceIntent(token);
      setIntent(isPlan ? 'setup' : 'payment');
      setClientSecret(res.data.clientSecret);
    } catch (err) {
      const reported = err?.response?.data?.invoice;
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.msg;
      if (reported) {
        setInvoice(reported);
        setState(payStateForInvoice(reported));
      } else if (isPlan && [400, 422].includes(status) && serverMsg) {
        setStartError(serverMsg);
      } else if ([404, 429, 422].includes(status)) {
        setState(payStateForError(err));
      } else {
        setStartError('We could not start the payment. Please try again.');
      }
    } finally {
      setStarting(false);
    }
  };

  const message = MESSAGES[state];
  const progress = invoice?.planProgress || { paid: 0, total: 0, nextPaymentDate: null };

  return (
    <EditorialPageShell title="Pay invoice" description="Pay your Kehilat Bnei Torah invoice securely.">
      <EditorialSection>
        <Box sx={{ maxWidth: 560, mx: 'auto', py: { xs: 6, md: 10 }, px: 2 }}>
          {state === PAY_STATES.LOADING && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress aria-label="Loading invoice" sx={{ color: 'var(--navy-500)' }} />
            </Box>
          )}

          {state === PAY_STATES.PLAN_ACTIVE && (
            <Stack spacing={1} sx={{ mb: 4 }}>
              <Typography variant="h5" component="h1">
                Your payment plan is active.
              </Typography>
              <Typography>
                {progress.nextPaymentDate
                  ? `${progress.paid} of ${progress.total} payments received. Next payment on ${longDateYmd(progress.nextPaymentDate)}.`
                  : 'All payments received.'}
              </Typography>
            </Stack>
          )}

          {message && (
            <Stack spacing={1} sx={{ mb: invoice ? 4 : 0 }}>
              <Typography variant="h5" component="h1">
                {message.title}
              </Typography>
              <Typography>{message.body}</Typography>
            </Stack>
          )}

          {invoice && state !== PAY_STATES.INVALID && (
            <InvoiceSummary
              invoice={invoice}
              asH1={!message && state !== PAY_STATES.PLAN_ACTIVE}
              totalLabel={state === PAY_STATES.PAYABLE ? 'Total due' : 'Total'}
            />
          )}

          {state === PAY_STATES.PAYABLE && (
            <Box sx={{ mt: 4 }}>
              {clientSecret ? (
                <Stack spacing={2}>
                  {intent === 'setup' && plan.option && (
                    <Typography>
                      {`${amountsText(plan.option)}; first payment on ${longDateYmd(startDate)}, then ${monthlyText(startDate)}.`}
                    </Typography>
                  )}
                  <InvoiceCheckout
                    clientSecret={clientSecret}
                    paymentMethodOrder={ACH_FIRST}
                    returnPath={intent === 'setup' ? RETURN_PATHS.payLinkPlan : RETURN_PATHS.payLink}
                    intent={intent}
                  />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {plan.options.length > 0 && (
                    <FormControl>
                      <FormLabel id="pay-choice-label">How would you like to pay?</FormLabel>
                      <RadioGroup
                        aria-labelledby="pay-choice-label"
                        value={String(plan.choice)}
                        onChange={(e) => {
                          const v = e.target.value;
                          plan.setChoice(v === 'full' || v === 'custom' ? v : Number(v));
                        }}
                      >
                        <FormControlLabel value="full" control={<Radio />} label="Pay in full" />
                        {PRESETS.filter((n) => plan.options.some((o) => o.installments === n)).map((n) => (
                          <FormControlLabel key={n} value={String(n)} control={<Radio />} label={`${n} monthly payments`} />
                        ))}
                        <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                      </RadioGroup>
                      {plan.choice === 'custom' && (
                        <TextField
                          type="number"
                          size="small"
                          value={plan.customCount}
                          onChange={(e) => plan.setCustomCount(e.target.value)}
                          inputProps={{ min: 1, max: 12, 'aria-label': 'Number of payments' }}
                          sx={{ maxWidth: 160 }}
                        />
                      )}
                    </FormControl>
                  )}

                  {plan.option && (
                    <Stack spacing={1}>
                      <TextField
                        type="date"
                        label="First payment date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: today, max: addDays(today, MAX_START_DAYS), 'aria-label': 'First payment date' }}
                        error={!dateOk}
                        helperText={dateOk ? undefined : 'Choose a date from today through 60 days from now.'}
                        sx={{ maxWidth: 220 }}
                      />
                      {dateOk && (
                        <>
                          <Typography>{amountsText(plan.option)}</Typography>
                          <Typography>{`First payment on ${longDateYmd(startDate)}, then ${monthlyText(startDate)}.`}</Typography>
                        </>
                      )}
                    </Stack>
                  )}

                  {plan.error && (
                    <Typography role="alert" color="error">
                      {plan.error}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    onClick={startPayment}
                    disabled={starting || Boolean(plan.error) || (Boolean(plan.option) && !dateOk)}
                  >
                    {starting ? 'Starting…' : plan.installments ? 'Continue to set up plan' : 'Continue to payment'}
                  </Button>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    Pay by bank transfer or card. No account or password needed.
                  </Typography>
                  {startError && (
                    <Typography role="alert" color="error">
                      {startError}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </EditorialSection>
    </EditorialPageShell>
  );
}
